/* WroeTrends product-page colour swatches - linked products only when native colour variants are absent */
(() => {
  if (window.__wtProductPageSwatchesLoaded) return;
  window.__wtProductPageSwatchesLoaded = true;

  const WRAP_CLASS = 'wt-product-page-swatches';
  const STYLE_ID = 'wt-product-page-swatches-style';

  const colourMap = {
    black: '#171717', white: '#f7f7f3', 'off white': '#eee9dd', cream: '#efe4cf',
    beige: '#d8c5a5', sand: '#d7c3a2', nude: '#d6bca6', stone: '#b9b1a5',
    taupe: '#a9967c', grey: '#b8bcc2', gray: '#b8bcc2', charcoal: '#55585d',
    silver: '#b9bec5', green: '#486a50', spruce: '#345443', 'spruce green': '#345443',
    olive: '#69714d', khaki: '#9d916c', teal: '#2e7473', blue: '#568fc4',
    'ocean blue': '#467da6', navy: '#24344f', 'royal blue': '#315eaa', red: '#b42b32',
    maroon: '#6e2835', burgundy: '#722f3b', brown: '#78523b', chocolate: '#5f4030',
    espresso: '#46352f', 'faded brown': '#8a6b57', purple: '#76558f', pink: '#df7892',
    'hot pink': '#df4d86', orange: '#d67b42', yellow: '#d4b847', gold: '#b6923d', neon: '#b7df45'
  };

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .${WRAP_CLASS} { margin: 1.8rem 0 1.7rem; }
      .${WRAP_CLASS}__label { display: block; margin: 0 0 .9rem; }
      .${WRAP_CLASS}__items {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: .9rem;
        min-height: 2.8rem;
      }
      .${WRAP_CLASS} a.${WRAP_CLASS}__item {
        display: block !important;
        width: 2.5rem;
        height: 2.5rem;
        border-radius: 50%;
        border: .1rem solid rgba(var(--color-foreground), .24);
        background: var(--wt-swatch, #d2d2d2);
        text-decoration: none;
        flex: 0 0 auto;
      }
      .${WRAP_CLASS} a.${WRAP_CLASS}__item.is-active {
        outline: .1rem solid rgba(var(--color-foreground), .9);
        outline-offset: .3rem;
      }
      .${WRAP_CLASS} a.${WRAP_CLASS}__item:focus-visible {
        outline: .2rem solid rgba(var(--color-foreground), 1);
        outline-offset: .3rem;
      }
      @media screen and (max-width: 749px) {
        .${WRAP_CLASS} { margin-top: 1.5rem; margin-bottom: 1.5rem; }
        .${WRAP_CLASS}__items { gap: .8rem; }
        .${WRAP_CLASS} a.${WRAP_CLASS}__item { width: 2.4rem; height: 2.4rem; }
      }
    `;
    document.head.appendChild(style);
  }

  function normalize(value) {
    return String(value || '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function singleColour(value) {
    const n = normalize(value);
    if (colourMap[n]) return colourMap[n];
    const key = Object.keys(colourMap)
      .sort((a, b) => b.length - a.length)
      .find((item) => n.includes(item));
    return key ? colourMap[key] : null;
  }

  function looksLikeColour(value) {
    const n = normalize(value);
    if (!n || n.length > 32) return false;
    if (singleColour(n)) return true;
    return n.split(/\s*(?:\/|&|\+)\s*/).some((part) => singleColour(part));
  }

  function swatchBackground(value) {
    const parts = String(value || '').split(/\s*(?:\/|&|\+)\s*/).filter(Boolean);
    const colours = parts.map(singleColour).filter(Boolean);
    if (colours.length >= 2) {
      return `linear-gradient(135deg, ${colours[0]} 0 49%, ${colours[1]} 51% 100%)`;
    }
    return singleColour(value) || '#d2d2d2';
  }

  function splitTitle(title) {
    const clean = String(title || '').replace(/\s+/g, ' ').trim();
    const match = clean.match(/^(.*)\s+-\s+(.+)$/);
    if (!match) return { base: clean, colour: null };
    const colour = match[2].trim();
    if (!looksLikeColour(colour)) return { base: clean, colour: null };
    return { base: match[1].trim(), colour };
  }

  function cleanUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      url.searchParams.delete('variant');
      url.searchParams.delete('preview_theme_id');
      url.searchParams.delete('pb');
      return url.pathname.replace(/\/$/, '');
    } catch (error) {
      return String(value || '');
    }
  }

  function sameUrl(a, b) {
    return cleanUrl(a) === cleanUrl(b);
  }

  function preservePreview(href) {
    try {
      const url = new URL(href, window.location.origin);
      const current = new URL(window.location.href);
      const previewThemeId = current.searchParams.get('preview_theme_id');
      const previewBar = current.searchParams.get('pb');
      if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
      if (previewBar !== null) url.searchParams.set('pb', previewBar);
      return url.href;
    } catch (error) {
      return href;
    }
  }

  function uniqueItems(items, activeHref, activeColour) {
    const output = [];
    const seen = new Set();
    const ordered = [...items].sort((a, b) => {
      const aActive = sameUrl(a.href, activeHref) || (activeColour && normalize(a.colour) === normalize(activeColour));
      const bActive = sameUrl(b.href, activeHref) || (activeColour && normalize(b.colour) === normalize(activeColour));
      return Number(bActive) - Number(aActive);
    });

    ordered.forEach((item) => {
      const key = normalize(item.colour);
      if (!item.colour || !item.href || seen.has(key)) return;
      seen.add(key);
      output.push(item);
    });
    return output;
  }

  function createSwatches(items, activeHref, activeColour) {
    const unique = uniqueItems(items, activeHref, activeColour);
    if (unique.length <= 1) return null;

    const wrap = document.createElement('div');
    wrap.className = WRAP_CLASS;
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', 'Available colours');

    const label = document.createElement('span');
    label.className = `form__label ${WRAP_CLASS}__label`;
    label.textContent = 'Colour';
    wrap.appendChild(label);

    const row = document.createElement('div');
    row.className = `${WRAP_CLASS}__items`;

    unique.forEach((item) => {
      const link = document.createElement('a');
      link.className = `${WRAP_CLASS}__item`;
      const isActive = sameUrl(item.href, activeHref) && (!activeColour || normalize(item.colour) === normalize(activeColour));
      if (isActive) {
        link.classList.add('is-active');
        link.setAttribute('aria-current', 'true');
      }
      link.href = preservePreview(item.href);
      link.title = item.colour;
      link.setAttribute('aria-label', item.colour);
      link.style.setProperty('--wt-swatch', swatchBackground(item.colour));

      const accessibleLabel = document.createElement('span');
      accessibleLabel.className = 'visually-hidden';
      accessibleLabel.textContent = item.colour;
      link.appendChild(accessibleLabel);
      row.appendChild(link);
    });

    wrap.appendChild(row);
    return wrap;
  }

  function insertSwatches(productInfo, swatches) {
    if (!swatches || productInfo.querySelector(`.${WRAP_CLASS}`)) return false;
    const variantPicker = productInfo.querySelector('variant-selects');
    if (variantPicker) {
      variantPicker.before(swatches);
      return true;
    }
    const productForm = productInfo.querySelector('product-form');
    if (productForm) {
      productForm.before(swatches);
      return true;
    }
    const infoContainer = productInfo.querySelector('.product__info-container') || productInfo;
    const price = infoContainer.querySelector('[id^="price-"]');
    if (price) price.after(swatches);
    else infoContainer.appendChild(swatches);
    return true;
  }

  function parseSiblingLinksFromHtml(html, baseKey) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const links = Array.from(doc.querySelectorAll('.card__heading a[href*="/products/"], a.full-unstyled-link[href*="/products/"]'));
      const seenUrls = new Set();
      return links.map((link) => {
        const href = link.href || link.getAttribute('href');
        const urlKey = cleanUrl(href);
        if (!href || seenUrls.has(urlKey)) return null;
        seenUrls.add(urlKey);
        const parsed = splitTitle(link.textContent);
        return { baseKey: normalize(parsed.base), colour: parsed.colour, href };
      }).filter((item) => item && item.colour && item.baseKey === baseKey);
    } catch (error) {
      return [];
    }
  }

  async function searchSiblingProducts(base, baseKey) {
    const root = window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
    const current = new URL(window.location.href);
    const searchParams = new URLSearchParams();
    searchParams.set('q', `"${base}"`);
    searchParams.set('type', 'product');
    const previewThemeId = current.searchParams.get('preview_theme_id');
    const previewBar = current.searchParams.get('pb');
    if (previewThemeId) searchParams.set('preview_theme_id', previewThemeId);
    if (previewBar !== null) searchParams.set('pb', previewBar);

    let htmlMatches = [];
    try {
      const response = await fetch(`${root}search?${searchParams.toString()}`, {
        headers: { Accept: 'text/html' }
      });
      if (response.ok) htmlMatches = parseSiblingLinksFromHtml(await response.text(), baseKey);
    } catch (error) {}

    let predictiveMatches = [];
    try {
      const params = new URLSearchParams();
      params.set('q', `"${base}"`);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '10');
      params.set('resources[options][unavailable_products]', 'show');
      params.set('resources[options][fields]', 'title');
      const response = await fetch(`${root}search/suggest.json?${params.toString()}`, {
        headers: { Accept: 'application/json' }
      });
      if (response.ok) {
        const json = await response.json();
        predictiveMatches = (json?.resources?.results?.products || []).map((product) => {
          const parsed = splitTitle(product.title);
          return { baseKey: normalize(parsed.base), colour: parsed.colour, href: product.url };
        }).filter((item) => item.colour && item.href && item.baseKey === baseKey);
      }
    } catch (error) {}

    const seen = new Set();
    return [...htmlMatches, ...predictiveMatches].filter((item) => {
      const key = `${normalize(item.colour)}|${cleanUrl(item.href)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  async function hasNativeMultiColourVariants(activeHref) {
    try {
      const url = new URL(activeHref, window.location.origin);
      const productPath = url.pathname.replace(/\/$/, '');
      const response = await fetch(`${productPath}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return false;

      const product = await response.json();
      const optionNames = Array.isArray(product.options)
        ? product.options.map((option) => typeof option === 'string' ? option : option?.name)
        : [];
      const colourIndex = optionNames.findIndex((name) => /^(colou?r)$/i.test(String(name || '').trim()));
      if (colourIndex < 0 || !Array.isArray(product.variants)) return false;

      const colours = new Set();
      product.variants.forEach((variant) => {
        const colour = variant.options?.[colourIndex] ?? variant[`option${colourIndex + 1}`];
        if (colour) colours.add(normalize(colour));
      });
      return colours.size > 1;
    } catch (error) {
      return false;
    }
  }

  async function renderProductSwatches() {
    injectStyles();
    const productInfo = document.querySelector('product-info') || document.querySelector('.product__info-container');
    if (!productInfo || productInfo.querySelector(`.${WRAP_CLASS}`)) return;

    const titleElement = productInfo.querySelector('.product__title h1') || productInfo.querySelector('h1');
    if (!titleElement) return;

    const activeHref = window.location.href;

    // If Shopify already supplies multiple native Colour/Color variants, keep its
    // built-in picker as the only colour selector and do not add linked swatches.
    if (await hasNativeMultiColourVariants(activeHref)) return;

    const title = titleElement.textContent.replace(/\s+/g, ' ').trim();
    const parsed = splitTitle(title);
    if (!parsed.colour) return;

    const siblings = await searchSiblingProducts(parsed.base, normalize(parsed.base));
    const withoutDuplicateActiveColour = siblings.filter((item) => {
      return normalize(item.colour) !== normalize(parsed.colour) || sameUrl(item.href, activeHref);
    });

    if (!withoutDuplicateActiveColour.some((item) => sameUrl(item.href, activeHref))) {
      withoutDuplicateActiveColour.unshift({ colour: parsed.colour, href: activeHref });
    }

    const swatches = createSwatches(withoutDuplicateActiveColour, activeHref, parsed.colour);
    insertSwatches(productInfo, swatches);
  }

  function start() {
    renderProductSwatches();
    document.addEventListener('shopify:section:load', renderProductSwatches);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
